// react
import { useCallback, useMemo, useState, useEffect, ReactNode } from 'react';

// mui
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// models
import {
  ActivityLogItem,
  CareCircle,
  CareProfileCategoryID,
  CaseManagerSummary,
  OrganiseEntryStatus,
  Section,
  Shortlist,
  ShortlistItem,
  ShortlistItemStatus,
  computeFormStatus,
} from '@karehero/models';
import { getInitials, formatFullName } from '@karehero/helpers/name';

// templates / components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

import EditableCareCircleSummary from '@/pwa/organisms/EditableCareCircleSummary/EditableCareCircleSummary';
import FormEditAccordion from '@/pwa/organisms/FormEditAccordion/FormEditAccordion';
import { OrganiserEntryGroupList } from '@/pwa/organisms/OrganiserEntryGroupList/OrganiserEntryGroupList';
import { OrganiserEntryItem } from '@/pwa/organisms/OrganiserEntryRow/OrganiserEntryRow';
import ShortlistsDrawer from '@/pwa/organisms/ShortlistsDrawer/ShortlistsDrawer';
import ShortlistForm from '@/pwa/organisms/ShortlistForm/ShortlistForm';
import DialogWithConfirmActions from '@/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';
import { Icon } from '@/components/atoms/Icon';
import { ActivityLog } from './ActivityLog';
import DismissShortlistItemForm from '@/pwa/organisms/DismissShortlistItemForm/DismissShortlistItemForm';

export interface CategoryData {
  categoryID: CareProfileCategoryID | string;
  title: string;
  category?: any;
  sections?: Section[];
  forms?: any;
  answers?: any;
  setAnswers: any;
  createManyAnswer: any;
  handlePrintPdf?: () => void;
}

const FORM_DISPLAY: { id: CareProfileCategoryID; title: string }[] = [
  {
    id: CareProfileCategoryID.EligibilityCalculator,
    title: 'Benefits eligibility',
  },
  { id: CareProfileCategoryID.CareAssessment, title: 'Care assessment' },
  { id: CareProfileCategoryID.FinancialStatus, title: 'Financial status' },
  {
    id: CareProfileCategoryID.AttendanceAllowance,
    title: 'Attendance allowance',
  },
];

const FormsPanel = ({
  categoryData,
  isLoading,
}: {
  categoryData: CategoryData[];
  isLoading?: boolean;
}) => {
  const [expandedCategoryID, setExpandedCategoryID] = useState<string | null>(
    null,
  );

  return (
    <Stack>
      {FORM_DISPLAY.map((display, i) => {
        const data = categoryData.find((d) => d.categoryID === display.id);
        const status = computeFormStatus(data?.sections);
        const isExpanded = expandedCategoryID === display.id;
        return (
          <FormEditAccordion
            key={display.id}
            title={display.title}
            status={status}
            data={data}
            isExpanded={isExpanded}
            onToggle={() =>
              setExpandedCategoryID(isExpanded ? null : display.id)
            }
            isLoading={isLoading}
          />
        );
      })}
    </Stack>
  );
};

const CaseManagerLabel = ({ person }: { person: CaseManagerSummary }) => {
  const name = formatFullName(person.firstName, person.lastName);
  return (
    <Stack
      direction='row'
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      <Chip size='small' label={getInitials(name)} />
      <Typography variant='body2'>{name}</Typography>
    </Stack>
  );
};

const CaseManagementCard = ({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items?: CaseManagerSummary[] | null;
  emptyLabel: string;
}) => (
  <Card variant='outlined'>
    <Stack
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.5,
      }}
    >
      <Typography
        variant='subtitle2'
        sx={{
          color: 'text.title',
        }}
      >
        {title}
      </Typography>
      {items && items.length > 0 ? (
        <Stack spacing={1}>
          {items.map((person) => (
            <CaseManagerLabel key={person.accountId} person={person} />
          ))}
        </Stack>
      ) : (
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          {emptyLabel}
        </Typography>
      )}
    </Stack>
  </Card>
);

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card variant='outlined' sx={{ width: '100%' }}>
    <Typography
      variant='h5'
      sx={{
        color: 'text.title',
        px: 1.5,
        py: 1,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        p: 2,
      }}
    >
      {children}
    </Box>
  </Card>
);

interface DrawerProps {
  title: string;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (val: boolean) => void;
  children?: ReactNode;
}

const Drawer = ({
  title,
  isDrawerOpen,
  setIsDrawerOpen,
  children,
}: DrawerProps) => {
  const theme = useTheme();

  // The chevron points the way the card moves: out of the way to collapse,
  // back in to expand. Mirrored for RTL.
  const CollapseChevron =
    theme.direction === 'ltr' ? ChevronRightIcon : ChevronLeftIcon;
  const ExpandChevron =
    theme.direction === 'ltr' ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <Card
      // Full height of the care-search row, fixed to the drawer width when
      // open; collapsed it hugs the chevron. Flex column so the shortlist list
      // scrolls inside the card rather than growing it.
      sx={{
        width: isDrawerOpen ? drawerWidth : 'auto',
        height: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          padding: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          {/* Collapsed, this is the only thing left in the card — it keeps the
              same place in the same padded header, so expanding doesn't shift
              anything. */}
          <IconButton
            aria-label={
              isDrawerOpen ? 'collapse shortlists' : 'expand shortlists'
            }
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          >
            {isDrawerOpen ? <CollapseChevron /> : <ExpandChevron />}
          </IconButton>
          {/* h4 is the theme's largest Work Sans heading — h1-h3 are Lora */}
          {isDrawerOpen && <Typography variant='h4'>{title}</Typography>}
        </Stack>
      </Box>
      {isDrawerOpen && children}
    </Card>
  );
};

interface TabNavigationProps {
  tabs: string[];
  currentTab: string;
  setCurrentTab: (newTab: string) => void;
}
const TabNavigation = ({
  tabs,
  currentTab,
  setCurrentTab,
}: TabNavigationProps) => {
  const currentTabIndex = useMemo(
    () => tabs.findIndex((tabName) => tabName === currentTab) || 0,
    [tabs, currentTab],
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      if (newValue >= 0 && tabs.length > newValue) {
        setCurrentTab(tabs[newValue]);
      }
    },
    [setCurrentTab],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
      }}
    >
      <Tabs
        value={currentTabIndex}
        onChange={handleChange}
        aria-label='tab-navigation'
      >
        {tabs.map((tabName) => (
          <Tab key={tabName} label={tabName} />
        ))}
      </Tabs>
    </Box>
  );
};

interface ShortlistID {
  careCircleID: string;
  id: string;
}

export interface AdminCareCircleViewProps {
  isLoading?: boolean;
  onBack: () => void;
  careCircle?: CareCircle;
  categoryData: CategoryData[];
  linkedCircles: {
    name: string;
    onClick?: () => void;
  }[];
  entries: OrganiserEntryItem[];
  onStatusChange: (entryId: string, status: OrganiseEntryStatus) => void;
  onEntryClick: (entryId: string) => void;
  onAddEntry: () => void;
  onEntryLinkClick?: (link: string) => void;
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  onShowMore?: (currentlyVisible: number, totalUpcoming: number) => void;
  careSearchContent?: (ctx: {
    onOpenCreateShortlistForm: () => void;
  }) => React.ReactNode;
  shortlists?: Shortlist[];
  onCreateShortlist: ({
    careCircleID,
    shortlist,
  }: {
    careCircleID: string;
    shortlist: Shortlist;
  }) => Promise<Shortlist | undefined>;
  onUpdateShortlist: ({
    id,
    careCircleID,
    params,
  }: {
    id: string;
    careCircleID: string;
    params: Partial<Shortlist>;
  }) => void;
  onArchiveShortlist: (res: ShortlistID) => void;
  onPublishShortlist: (res: ShortlistID) => void;
  shortlistID: string | null;
  setShortlistID: (id: string | null) => void;
  shortlistItems?: ShortlistItem[];
  onItemStatusChange?: (
    regulatoryId: string,
    status: ShortlistItemStatus,
  ) => void;
  onItemDismiss?: (
    regulatoryId: string,
    reason: string,
    detail?: string | null,
  ) => void;
  onItemRestore?: (regulatoryId: string) => void;
  onItemClick?: (regulatoryId: string) => void;
  onItemNoteSave?: (
    regulatoryId: string,
    note: string,
  ) => Promise<boolean> | void;
  documentsContent?: React.ReactNode;
  tab?: AdminTab;
  onTabChange?: (tab: AdminTab) => void;
  onAddToCaseload?: () => void;
  onRemoveFromCaseload?: () => void;
  onMakePrimary?: () => void;
  caseManager?: CaseManagerSummary | null;
  watchers?: CaseManagerSummary[];
  activityItems?: ActivityLogItem[];
  isActivityLoading?: boolean;
  hasMoreActivity?: boolean;
  onLoadMoreActivity?: () => void;
  onRefreshActivity?: () => void;
  onActivityCareCircleClick?: (careCircleId: string) => void;
  getActivityItemHref?: (item: ActivityLogItem) => string | undefined;
  onActivityItemClick?: (item: ActivityLogItem) => void;
}

export enum AdminTab {
  Overview = 'Overview',
  Activity = 'Activity',
  Actions = 'Actions',
  Documents = 'Documents',
  Forms = 'Forms',
  CareSearch = 'Care search',
}

const TABS = Object.values(AdminTab);

const drawerWidth = '380px';

/**
 * AdminCareCircleView: care-circle control panel for admins. Shows the
 * care recipient profile, an Actions panel, a Forms panel (with inline edit
 * preserved on chevron expand), and the existing CareHome shortlist below.
 */
export const AdminCareCircleView = ({
  isLoading,
  onBack,
  careCircle,
  categoryData,
  linkedCircles,
  entries,
  onStatusChange,
  onEntryClick,
  onEntryLinkClick,
  onAddEntry,
  showCompleted,
  onToggleShowCompleted,
  onShowMore,
  careSearchContent,
  shortlists,
  onCreateShortlist,
  onArchiveShortlist,
  onPublishShortlist,
  onUpdateShortlist,
  shortlistID,
  setShortlistID,
  shortlistItems,
  onItemStatusChange,
  onItemDismiss,
  onItemRestore,
  onItemClick,
  onItemNoteSave,
  documentsContent,
  tab,
  onTabChange,
  onAddToCaseload,
  onRemoveFromCaseload,
  onMakePrimary,
  caseManager,
  watchers,
  activityItems,
  isActivityLoading,
  hasMoreActivity,
  onLoadMoreActivity,
  onRefreshActivity,
  onActivityCareCircleClick,
  getActivityItemHref,
  onActivityItemClick,
}: AdminCareCircleViewProps) => {
  const [dismissingItemID, setDismissingItemID] = useState<string | null>(null);
  const [isShortlistFormOpen, setIsShortlistFormOpen] = useState(false);
  const [isEditShortlistFormOpen, setIsEditShortlistFormOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<string | null>(null);
  const [dismissDetail, setDismissDetail] = useState<string | null>(null);
  const currentTab = tab ?? AdminTab.Overview;
  const setCurrentTab = useCallback(
    (t: string) => {
      onTabChange?.(t as AdminTab);
    },
    [onTabChange],
  );
  const [shortlistName, setShortlistName] = useState('');
  const activeShortlists = useMemo(
    () => shortlists?.filter((shortlist) => !shortlist.isArchived),
    [shortlists],
  );
  const selectedShortlist = useMemo(
    () => shortlists?.find((shortlist) => shortlist.id === shortlistID),
    [shortlists, shortlistID],
  );

  const onEditShortlist = useCallback(
    (id: string) => {
      const editShortlist = shortlists?.find(
        (shortlist) => shortlist.id === id,
      );
      setIsEditShortlistFormOpen(true);
      setShortlistID(id);
      setShortlistName(editShortlist?.name || '');
    },
    [setIsEditShortlistFormOpen, shortlists, setShortlistID, setShortlistName],
  );

  const createAndActivate = useCallback(
    async ({
      careCircleID,
      shortlist,
    }: {
      careCircleID: string;
      shortlist: Shortlist;
    }) => {
      const res = await onCreateShortlist({ careCircleID, shortlist });
      if (res) {
        setShortlistID(res.id);
      }
    },
    [onCreateShortlist, setShortlistID],
  );

  // Pull care-assessment answers for header derivations.
  const careAssessmentData = useMemo(
    () =>
      categoryData.find(
        (d) => d.categoryID === CareProfileCategoryID.CareAssessment,
      ),
    [categoryData],
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    setIsDrawerOpen(currentTab === AdminTab.CareSearch);
  }, [currentTab]);

  // If no selected shortlist, and at least one shortlist exists, choose most recent
  useEffect(() => {
    if (!shortlistID && activeShortlists && activeShortlists.length > 0) {
      setShortlistID(activeShortlists[0].id);
    }
  }, [activeShortlists, shortlistID, selectedShortlist, setShortlistID]);

  useEffect(() => {
    if (
      selectedShortlist?.isArchived &&
      activeShortlists &&
      activeShortlists.length > 0
    ) {
      setShortlistID(activeShortlists[0].id);
    }
  }, [activeShortlists, selectedShortlist, setShortlistID]);

  return (
    <ModalPageTemplate onBack={onBack}>
      {/* Full-height column: the template hands us a definite height, so the
          care-search row below can take what's left and the shortlist card can
          run the full height of it. */}
      <Stack
        sx={{
          height: '100%',
          minHeight: 0,
        }}
      >
        {(onAddToCaseload || onRemoveFromCaseload) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 3,
              pt: 1,
            }}
          >
            {onAddToCaseload && (
              <Button
                variant='outlined'
                size='small'
                startIcon={<Icon icon='bookmark' fontSize={14} />}
                onClick={onAddToCaseload}
              >
                Add to Caseload
              </Button>
            )}
            {onMakePrimary && (
              <Button variant='outlined' size='small' onClick={onMakePrimary}>
                Set as Primary
              </Button>
            )}
            {onRemoveFromCaseload && (
              <Button
                variant='outlined'
                size='small'
                color='error'
                onClick={onRemoveFromCaseload}
              >
                Remove from Caseload
              </Button>
            )}
          </Box>
        )}
        <TabNavigation
          tabs={TABS}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
        />
        <Box
          sx={{
            display: 'flex',
            p: 3,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* the tab content scrolls in its own column now that the row is
            height-bound, so the card beside it stays put */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'auto',
              pr: 2,
            }}
          >
            {currentTab === AdminTab.Overview && (
              <Stack
                direction='row'
                spacing={3}
                sx={{
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <EditableCareCircleSummary
                    careCircle={careCircle}
                    linkedCircles={linkedCircles}
                    careAssessmentData={careAssessmentData}
                  />
                </Box>
                <Stack spacing={2} sx={{ width: 280, flexShrink: 0 }}>
                  <CaseManagementCard
                    title='Case manager'
                    items={caseManager ? [caseManager] : null}
                    emptyLabel='Unassigned'
                  />
                  <CaseManagementCard
                    title='Watchers'
                    items={watchers}
                    emptyLabel='No watchers'
                  />
                </Stack>
              </Stack>
            )}

            {currentTab === AdminTab.Actions && (
              <SectionCard title='Actions'>
                <Stack
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component='span'
                    sx={{ display: 'block', width: '100%' }}
                  >
                    <Button
                      variant='contained'
                      size='medium'
                      fullWidth
                      onClick={onAddEntry}
                      disabled={false}
                    >
                      Add action
                    </Button>
                  </Box>
                  {onToggleShowCompleted && (
                    <Stack
                      direction='row'
                      sx={{
                        justifyContent: 'flex-end',
                        width: '100%',
                      }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        color='primary'
                        onClick={onToggleShowCompleted}
                      >
                        {showCompleted ? 'Hide completed' : 'Show completed'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <OrganiserEntryGroupList
                  entries={entries}
                  onStatusChange={onStatusChange}
                  onEntryClick={onEntryClick}
                  onEntryLinkClick={onEntryLinkClick}
                  showCompleted={showCompleted}
                  onShowMore={onShowMore}
                />
              </SectionCard>
            )}

            {currentTab === AdminTab.Activity && (
              <SectionCard title='Activity'>
                <ActivityLog
                  items={activityItems}
                  isLoading={isActivityLoading}
                  hasMore={hasMoreActivity}
                  onLoadMore={onLoadMoreActivity}
                  onRefresh={onRefreshActivity}
                  onCareCircleClick={onActivityCareCircleClick}
                  getItemHref={getActivityItemHref}
                  onItemClick={onActivityItemClick}
                />
              </SectionCard>
            )}

            {currentTab === AdminTab.Documents && documentsContent}

            {currentTab === AdminTab.Forms && (
              <SectionCard title='Forms'>
                <FormsPanel categoryData={categoryData} isLoading={isLoading} />
              </SectionCard>
            )}

            {currentTab === AdminTab.CareSearch &&
              careSearchContent?.({
                onOpenCreateShortlistForm: () => {
                  setShortlistName('');
                  setIsShortlistFormOpen(true);
                },
              })}
          </Box>
          {currentTab === AdminTab.CareSearch && (
            <Drawer
              isDrawerOpen={isDrawerOpen}
              setIsDrawerOpen={setIsDrawerOpen}
              title={`${careCircle?.careRecipientAccount?.firstName || ''}'s Shortlists`}
            >
              <ShortlistsDrawer
                shortlists={activeShortlists || []}
                careCircle={careCircle}
                onPublish={onPublishShortlist}
                onEdit={onEditShortlist}
                onCreate={() => {
                  setShortlistName('');
                  setIsShortlistFormOpen(true);
                }}
                shortlistID={shortlistID}
                setShortlistID={setShortlistID}
                items={shortlistItems}
                onItemStatusChange={onItemStatusChange}
                onItemDismiss={(regulatoryId) =>
                  setDismissingItemID(regulatoryId)
                }
                onItemRestore={onItemRestore}
                onItemClick={onItemClick}
                onItemNoteSave={onItemNoteSave}
              />
            </Drawer>
          )}
        </Box>
      </Stack>
      <DialogWithConfirmActions
        title={isEditShortlistFormOpen ? 'Edit shortlist' : 'Create shortlist'}
        isOpen={isShortlistFormOpen || isEditShortlistFormOpen}
        onClose={() => {
          setIsShortlistFormOpen(false);
          setIsEditShortlistFormOpen(false);
        }}
        actions={[
          isEditShortlistFormOpen && {
            label: 'Archive',
            color: 'warning',
            isConfirmable: true,
            warningText: 'This will remove the shortlist from the members view',
            confirmText: 'Archive',
            onAction: () => {
              shortlistID &&
                careCircle &&
                onArchiveShortlist({
                  careCircleID: careCircle.id,
                  id: shortlistID,
                });
              setIsShortlistFormOpen(false);
              setIsEditShortlistFormOpen(false);
            },
          },
          {
            label: 'Discard',
            onAction: () => {
              setIsShortlistFormOpen(false);
              setIsEditShortlistFormOpen(false);
            },
            variant: 'outlined',
          },
          !isEditShortlistFormOpen && {
            label: 'Create',
            isDisabled:
              shortlistName === '' ||
              !!shortlists?.find(
                (shortlist) =>
                  shortlist.name === shortlistName ||
                  shortlist.id === shortlistName,
              ),
            onAction: () => {
              careCircle &&
                createAndActivate({
                  careCircleID: careCircle.id,
                  shortlist: {
                    careCircleID: careCircle.id,
                    id: shortlistName,
                    name: shortlistName,
                  },
                });
              setIsShortlistFormOpen(false);
              setShortlistName('');
            },
            variant: 'contained',
          },
          isEditShortlistFormOpen && {
            label: 'Save',
            variant: 'contained',
            isDisabled:
              shortlistName === '' ||
              !!shortlists?.find(
                (shortlist) =>
                  shortlist.id !== shortlistID &&
                  (shortlist.name === shortlistName ||
                    shortlist.id === shortlistName),
              ),
            onAction: () => {
              setIsEditShortlistFormOpen(false);
              careCircle &&
                shortlistID &&
                onUpdateShortlist({
                  id: shortlistID,
                  careCircleID: careCircle.id,
                  params: { name: shortlistName },
                });
            },
          },
        ].filter((x) => !!x)}
      >
        <ShortlistForm
          name={shortlistName}
          setName={setShortlistName}
          isEditing={isEditShortlistFormOpen}
        />
      </DialogWithConfirmActions>
      <DialogWithConfirmActions
        title={'Dismiss shortlist item'}
        isOpen={dismissingItemID !== null}
        onClose={() => setDismissingItemID(null)}
        actions={[
          {
            label: 'Discard',
            onAction: () => {
              setDismissingItemID(null);
            },
          },
          {
            label: 'Dismiss',
            color: 'error',
            variant: 'contained',
            isDisabled: dismissReason === null,
            onAction: () => {
              dismissingItemID &&
                dismissReason &&
                onItemDismiss?.(dismissingItemID, dismissReason, dismissDetail);
              setDismissingItemID(null);
            },
          },
        ]}
      >
        <DismissShortlistItemForm
          providerName='Provider'
          regulatoryID='regID'
          reason={dismissReason}
          setReason={setDismissReason}
          detail={dismissDetail}
          setDetail={setDismissDetail}
        />
      </DialogWithConfirmActions>
    </ModalPageTemplate>
  );
};

AdminCareCircleView.displayName = 'AdminCareCircleView';
export default AdminCareCircleView;
